import { ThemedImage } from '@/domains/theme/ThemedImage';

import { Button, DetailM } from '@pensionbee/honeypot-ui';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePlatformInfo } from '@/domains/shared/hooks/useIsNative';
import Link from 'next/link';

type HeaderProps = {
  logoLink: string;
  backAction?: () => void;
  showBackButton?: boolean;
  showTooltip?: boolean;
  showAccountIcon?: boolean;
  toolTipAction?: () => void;
  backButtonIcon?: 'Back' | 'Close';
  dataTestId?: string;
  headerRightAction?: {
    label: string;
    onClick: () => void;
  };
};

export const Header = (props: HeaderProps) => {
  const {
    logoLink,
    backAction,
    showBackButton,
    showTooltip,
    showAccountIcon,
    toolTipAction,
    dataTestId,
    headerRightAction,
  } = props;

  const [isPhone, setIsPhone] = useState(true);
  const { isWeb: showDesktopLogo, isIOS: isIos } = usePlatformInfo();

  useEffect(() => {
    setIsPhone(window.innerWidth < 744);
  }, []);

  const backButtonIcon = props.backButtonIcon || 'Back';
  const iosPaddingTop = isPhone ? 64 : 48;
  const headerControlsClass = twMerge(
    'row-start-1 flex items-center justify-end gap-12',
    showAccountIcon
      ? 'col-start-11 col-end-13'
      : 'col-start-11 col-end-13 sm:col-start-9 sm:col-end-10',
  );

  return (
    <header
      data-testid={dataTestId}
      style={isIos ? { paddingTop: iosPaddingTop } : undefined}
      className={`bg-background fixed top-0 inset-x-0 z-50 max-w-header mx-auto grid grid-cols-12 min-h-48 sm:min-h-64 pb-12 ${isIos ? '' : 'pt-12 sm:pt-16'} px-20 sm:px-28 sm:pb-16 items-center`}
    >
      <div
        className={twMerge('col-span-3 hidden', showDesktopLogo && 'md:block')}
      >
        <a href={logoLink} aria-label="Go to PensionBee homepage">
          <ThemedImage
            name="pensionbeelogo-transparent"
            alt="PensionBee Logo"
            width={160}
            height={58}
          />
        </a>
      </div>
      {showBackButton && (
        <div className="col-span-2 sm:block">
          <Button
            className={`p-8 ${
              backButtonIcon === 'Back' ? '[&>svg]:size-24' : ''
            } w-36 h-36 sm:w-48 sm:h-48 sm:p-12`}
            variant="icon"
            onClick={backAction}
            icon={backButtonIcon}
            aria-label="Go back to previous page"
          />
        </div>
      )}

      {headerRightAction && (
        <div className="col-start-10 col-end-13 sm:col-start-8 md:col-end-10 row-start-1 flex items-center justify-end">
          <button
            type="button"
            onClick={headerRightAction.onClick}
            className="text-blue-100 underline cursor-pointer bg-transparent border-none p-0"
          >
            <DetailM>{headerRightAction.label}</DetailM>
          </button>
        </div>
      )}

      {(showAccountIcon || showTooltip) && (
        <div className={headerControlsClass}>
          {showTooltip && (
            <button
              type="button"
              onClick={toolTipAction}
              aria-label="Page info"
              className="cursor-pointer bg-transparent border-none p-0 shrink-0 hover:brightness-125"
            >
              <ThemedImage
                name="tooltip"
                alt="Tooltip"
                width={28}
                height={28}
                className="size-28"
              />
            </button>
          )}
          {showAccountIcon && (
            <Button
              ariaLabel="Go to Account page"
              as={Link}
              href="/hive/beehive/account"
              variant="icon"
              icon="AccountPerson"
            />
          )}
        </div>
      )}
    </header>
  );
};
